"use client";

import {
  Briefcase,
  Building2,
  ChevronRight,
  Clock,
  Database,
  Globe,
  Home,
  type LucideIcon,
  Mic,
  Users,
  Video,
} from "lucide-react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { ENABLE_ROLES_CMS } from "@/components/config/config";
import { cn } from "@/lib/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { AppStateContext } from "./context/AppContext";
import Image from "next/image";
import { formatResetDate, sidebarQuota, teamAccess } from "@/lib/recruiterOffers";
import ScheduleInterviewFilm from "@/components/copilot/ScheduleInterviewFilm";
import { useProUpgrade } from "@/hooks/useProUpgrade";
import { useTranslation } from "react-i18next";
import { dateLocale, msg } from "@/lib/i18n";

type NavLink = {
  title: string;
  icon: LucideIcon;
  href: string;
  requiresRolesCms?: boolean;
  novaTarget: string;
};

type NavChild = {
  title: string;
  href: string;
  requiresRolesCms?: boolean;
  novaTarget?: string;
};

type NavGroup = {
  title: string;
  icon: LucideIcon;
  children: NavChild[];
};

type MenuItem = NavLink | NavGroup;

const navItems: MenuItem[] = [
  { title: msg("Dashboard"), icon: Home, href: "/dashboard/agency", novaTarget: "nav.dashboard" },
  {
    title: msg("Career page"),
    icon: Globe,
    href: "/dashboard/agency/company/career-page",
    requiresRolesCms: true,
    novaTarget: "nav.careerpage",
  },
  {
    title: msg("Hiring"),
    icon: Briefcase,
    children: [
      { title: msg("Roles"), href: "/dashboard/agency/roles", requiresRolesCms: true, novaTarget: "nav.roles" },
      { title: msg("Screening"), href: "/dashboard/agency/screening", novaTarget: "nav.screening" },
      { title: msg("Interviews"), href: "/dashboard/agency/interviews", novaTarget: "nav.interviews" },
      { title: msg("Reports"), href: "/dashboard/agency/reports", novaTarget: "nav.reports" },
      // Accepted candidates, per role. Off the nav since 2026-08-03, back as it was.
      { title: msg("Review"), href: "/dashboard/agency/reviews" },
    ],
  },
  // Every call recording, in a role or not; /calls/:callId opens one.
  { title: msg("Recordings"), icon: Mic, href: "/dashboard/agency/calls", novaTarget: "nav.recordings" },
  {
    title: msg("Talent"),
    icon: Database,
    children: [
      { title: msg("Talent search"), href: "/dashboard/agency/talent-pool", novaTarget: "nav.talent" },
      { title: msg("Library"), href: "/dashboard/agency/assets", novaTarget: "nav.library" },
    ],
  },
  {
    title: msg("Company"),
    icon: Building2,
    children: [
      {
        title: msg("Emails"),
        href: "/dashboard/agency/company/emails",
        requiresRolesCms: true,
        novaTarget: "nav.emails",
      },
      { title: msg("Integrations"), href: "/dashboard/agency/company/integrations", novaTarget: "nav.integrations" },
    ],
  },
];

const menuItems: MenuItem[] = navItems.flatMap<MenuItem>((item) => {
  if (!("children" in item)) {
    return !item.requiresRolesCms || ENABLE_ROLES_CMS ? [item] : [];
  }

  const children = item.children.filter(
    (child) => !child.requiresRolesCms || ENABLE_ROLES_CMS
  );
  return children.length > 0 ? [{ ...item, children }] : [];
});

const isNavGroup = (item: MenuItem): item is NavGroup => "children" in item;

const isChildActive = (child: NavChild, pathname: string | null) =>
  pathname === child.href ||
  Boolean(pathname?.startsWith(`${child.href}/`)) ||
  (child.href === "/dashboard/agency/talent-pool" &&
    Boolean(pathname?.startsWith("/dashboard/agency/data")));

const isGroupActive = (group: NavGroup, pathname: string | null) =>
  group.children.some((child) => isChildActive(child, pathname));

export function AppSidebar() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const { userProfile, totalMinutes, usedMinutes } = useContext(AppStateContext);
  // What this account's allowance actually is, per the server. `interviewQuota`
  // is authoritative for recruiter accounts; a legacy or candidate account falls
  // back to the existing minutes widget untouched.
  const quota = sidebarQuota(userProfile);
  const team = teamAccess(userProfile);
  // With Stripe checkout on, "Upgrade to Pro" goes to Stripe's checkout;
  // without it the button keeps its /pricing link.
  const upgrade = useProUpgrade();

  // Whether to offer an upgrade. Prefer the server's offer code; fall back to
  // the Free plan row id for accounts the new contract has not reached yet.
  const isOnFreeOffer =
    userProfile?.interviewQuota?.offerCode === "free" ||
    (!userProfile?.interviewQuota && userProfile?.plan?.id === 1);

  const remainingMinutes =
    totalMinutes > 0 && usedMinutes <= totalMinutes ? totalMinutes - usedMinutes : 0;
  // An internal plan's allowance (999,999,999 seconds) means "no limit"; the
  // raw minute count reads as a mistake.
  const unlimitedMinutes = totalMinutes >= 1_000_000;

  const percentage = totalMinutes > 0
    ? Math.min((usedMinutes / totalMinutes) * 100, 100)
    : 0;
  const [isOpen, setIsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    () =>
      Object.fromEntries(
        menuItems
          .filter(isNavGroup)
          .map((group) => [group.title, isGroupActive(group, pathname)])
      )
  );
  const wrapperRef = useRef<HTMLDivElement>(null);

  const getTimeColor = () => {
    const percentage = (remainingMinutes / totalMinutes) * 100;
    if (percentage > 50) return "text-green-600";
    if (percentage > 25) return "text-yellow-600";
    return "text-red-600";
  };

  const getProgressColor = () => {
    const percentage = (remainingMinutes / totalMinutes) * 100;
    if (percentage > 50) return "bg-green-500";
    if (percentage > 25) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Same thresholds as the minutes widget, applied to interviews left.
  const remainingQuotaPercent = () =>
    quota.mode === "interviews" && quota.total > 0
      ? (quota.remaining / quota.total) * 100
      : 0;

  const getQuotaColor = () => {
    const percentage = remainingQuotaPercent();
    if (percentage > 50) return "text-green-600";
    if (percentage > 25) return "text-yellow-600";
    return "text-red-600";
  };

  const getQuotaProgressColor = () => {
    const percentage = remainingQuotaPercent();
    if (percentage > 50) return "bg-green-500";
    if (percentage > 25) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Close sidebar on outside click (mobile)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setExpandedGroups((current) => {
      const activeGroups = menuItems
        .filter(isNavGroup)
        .filter((group) => isGroupActive(group, pathname));

      if (activeGroups.every((group) => current[group.title])) return current;

      const next = { ...current };
      activeGroups.forEach((group) => {
        next[group.title] = true;
      });
      return next;
    });
  }, [pathname]);

  return (
    <div className="relative z-50">
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 md:hidden z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Toggle Button */}


      {/* Sidebar */}
      <div
        ref={wrapperRef}
        className={cn(
          "app-sidebar elevated-glass elevated-shadow bg-sidebar/70 w-64 h-full border-r border-sidebar-border/30 flex flex-col transform transition-transform duration-300 ease-in-out",
          "md:translate-x-0 md:relative md:z-0",
          isOpen ? "translate-x-0 absolute top-0 left-0 z-50" : "-translate-x-full absolute top-0 left-0 z-50"
        )}
      >
        <button
          className="md:hidden absolute top-4 -right-14 z-[60] w-8 h-6 flex flex-col justify-center items-center space-y-[4px]"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={t("Toggle menu")}
        >
          <span className={`block h-[2px] w-6 bg-black transition-all duration-300 ${isOpen ? "rotate-45 translate-y-[6px]" : ""}`} />
          <span className={`block h-[2px] w-6 bg-black transition-all duration-300 ${isOpen ? "opacity-0" : ""}`} />
          <span className={`block h-[2px] w-6 bg-black transition-all duration-300 ${isOpen ? "-rotate-45 -translate-y-[6px]" : ""}`} />
        </button>
        {/* Header */}
        <div className="flex items-center gap-3 p-6 border-b h-16">
          <Link
            href={userProfile ? "/dashboard/agency" : "/"}
            className="flex items-center space-x-2"
          >
            <div className="flex items-center justify-center">
              <Image src="/logo2.png" alt="logo" width={20} height={20} className="w-5" priority />
            </div>
            <span className="text-lg font-extrabold tracking-tight text-[#1c3038] dark:text-white">
              AI Interview Agents
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-auto py-4 px-3">
          <nav className="space-y-1">
            {menuItems.map((item) => {
              // Recordings is owner-only on the server; a team seat would only see an error.
              if (team.reason === "seat" && "href" in item && item.href === "/dashboard/agency/calls") return null;
              if ("children" in item) {
                const groupIsActive = isGroupActive(item, pathname);
                const isExpanded = Boolean(expandedGroups[item.title]);

                return (
                  <div key={item.title}>
                    <button
                      type="button"
                      // A collapsed group's children are not in the page at
                      // all, so Nova cannot highlight Library or Emails until
                      // the group is opened. This lets it open the group first.
                      data-nova-group={item.children
                        .map((child) => child.novaTarget)
                        .filter(Boolean)
                        .join(" ")}
                      aria-expanded={isExpanded}
                      data-active={groupIsActive}
                      onClick={() =>
                        setExpandedGroups((current) => ({
                          ...current,
                          [item.title]: !current[item.title],
                        }))
                      }
                      className={cn(
                        "app-nav-item flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
                        groupIsActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-accent/70 hover:text-primary"
                      )}
                    >
                      <item.icon
                        className={cn(
                          "h-5 w-5",
                          groupIsActive ? "text-primary" : "text-muted-foreground"
                        )}
                      />
                      <span>{t(item.title)}</span>
                      <ChevronRight
                        className={cn(
                          "ml-auto h-4 w-4 transition-transform",
                          isExpanded && "rotate-90"
                        )}
                      />
                    </button>

                    {isExpanded ? (
                      <div className="mt-1 space-y-1">
                        {item.children.map((child) => {
                          const childIsActive = isChildActive(child, pathname);
                          return (
                            <Link
                              key={child.href}
                              href={child.href}
                              data-nova-target={child.novaTarget}
                              data-active={childIsActive}
                              onClick={() => setIsOpen(false)}
                              className={cn(
                                "app-nav-child flex items-center rounded-xl py-2.5 pl-12 pr-4 text-[13px] font-medium transition-colors",
                                childIsActive
                                  ? "bg-primary/10 text-primary"
                                  : "text-muted-foreground hover:bg-accent/70 hover:text-primary"
                              )}
                            >
                              {t(child.title)}
                            </Link>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              }

              const isActive =
                pathname === item.href ||
                (item.href !== "/dashboard/agency" && pathname?.startsWith(`${item.href}/`)) ||
                // Folder detail lives at /data/:folderId but belongs to the
                // Talent Pool hub (its Library tab).
                (item.href === "/dashboard/agency/talent-pool" &&
                  pathname?.startsWith("/dashboard/agency/data"));
              return (
              <Link
                key={item.title}
                href={item.href}
                data-nova-target={item.novaTarget}
                data-active={Boolean(isActive)}
                onClick={() => setIsOpen(false)}
                className={cn(
                  "app-nav-item flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent/70 hover:text-primary"
                )}
              >
                <item.icon
                  className={cn(
                    "h-5 w-5",
                    isActive ? "text-primary" : "text-muted-foreground"
                  )}
                />
                <span>{t(item.title)}</span>
              </Link>
              );
            })}
          </nav>
        </div>

        {/* The one-minute walkthrough, for whenever someone wants it again. */}
        <div className="px-3 pb-3">
          <ScheduleInterviewFilm variant="row" placement="sidebar" />
        </div>

        {/* Usage Summary */}
        <div className="p-4 border-t border-b">
          {quota.mode === "interviews" ? (
            // Interviews are the unit the plan is actually sold and metered in.
            // The old widget showed a daily minute pool, which was misleading:
            // the allowance is a monthly COUNT, a short interview still costs a
            // whole one, and nothing rolls over.
            <div
              data-testid="sidebar-interview-quota"
              className="bg-card/90 rounded-2xl p-3 border border-border/50"
            >
              <div className="flex items-center gap-2 mb-4">
                <Video className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-foreground">{t("Interviews this month")}</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span
                    data-testid="sidebar-interviews-remaining"
                    className={cn("text-sm font-bold", getQuotaColor())}
                  >
                    {t("{{count}} left", { count: quota.remaining })}
                  </span>
                  <span className="text-xs text-muted-foreground text-right">
                    {t("of {{total}}", { total: quota.total })}
                  </span>
                </div>

                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={cn("h-2 rounded-full transition-all duration-300", getQuotaProgressColor())}
                    style={{ width: `${Math.max(5, quota.usedPercent)}%` }}
                  />
                </div>

                {quota.held > 0 && (
                  <div
                    data-testid="sidebar-interviews-held"
                    className="text-xs text-muted-foreground text-center"
                  >
                    {t("{{count}} scheduled, not started yet", { count: quota.held })}
                  </div>
                )}

                <div className="text-xs text-muted-foreground text-center">
                  {t("Up to {{count}} minutes each", { count: quota.maxMinutes, defaultValue_one: "Up to {{count}} minute each" })}
                </div>

                {quota.unconfigured ? (
                  <div
                    data-testid="sidebar-quota-unconfigured"
                    className="text-xs text-amber-700 text-center"
                  >
                    {t("No allowance set on this plan yet. Contact us to set it up.")}
                  </div>
                ) : (
                  formatResetDate(quota.nextResetAt, dateLocale("en-GB")) && (
                    <div
                      data-testid="sidebar-quota-renewal"
                      className="text-xs text-muted-foreground text-center"
                    >
                      {t("Renews {{date}}", { date: formatResetDate(quota.nextResetAt, dateLocale("en-GB")) })}
                    </div>
                  )
                )}
              </div>
            </div>
          ) : quota.mode === "unlimited" ? (
            <div
              data-testid="sidebar-interview-quota"
              className="bg-card/90 rounded-2xl p-3 border border-border/50"
            >
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-foreground">
                  {t("Unlimited interviews")}
                </span>
              </div>
            </div>
          ) : (
            // Legacy accounts and candidate profiles keep the minutes widget
            // exactly as it was. Their quota is still measured in seconds server
            // side, so showing them an interview count would be wrong.
            <div
              data-testid="sidebar-legacy-minutes"
              className="bg-card/90 rounded-2xl p-3 border border-border/50"
            >
              <div className="flex items-center gap-2 mb-4">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold text-foreground">{t("Usage Summary (Minutes)")}</span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={cn("text-sm font-bold", getTimeColor())}>
                    {t("{{count}} used", { count: usedMinutes })}
                  </span>
                  <span className="text-xs text-muted-foreground text-right">
                    {unlimitedMinutes ? t("Unlimited") : t("of {{total}} total", { total: totalMinutes })}
                  </span>
                </div>

                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={cn("h-2 rounded-full transition-all duration-300", getProgressColor())}
                    style={{
                      width: `${Math.max(5, percentage)}%`,
                    }}
                  />
                </div>

                <div className="text-xs text-muted-foreground text-center">
                  {unlimitedMinutes
                    ? t("No minute limit on this plan")
                    : t("{{count}} minutes left", { count: remainingMinutes, defaultValue_one: "{{count}} minute left" })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isOnFreeOffer && (
          <div className="p-4 border-t">
            <Link
              href={"/pricing"}
              onClick={(event) => {
                if (!upgrade.enabled) return;
                event.preventDefault();
                void upgrade.startUpgrade("sidebar");
              }}
            >
              <Button disabled={upgrade.busy} className="app-upgrade w-full relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 hover:from-blue-700 hover:via-indigo-600 hover:to-purple-700 text-white py-2.5 h-auto font-medium text-sm rounded-lg shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group">
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-400 to-purple-400 opacity-30 blur-xl group-hover:opacity-40 transition-opacity duration-300" />
                <span className="relative flex items-center justify-center gap-2">
                  <span className="app-upgrade-mark text-yellow-300 text-lg">✨</span>
                  {t("Upgrade to Pro")}
                </span>
              </Button>
            </Link>
          </div>
        )}
        {/*
          Tenant team management, not staff admin: /dashboard/admin lists this
          account's own seats and creates more under it. It is shown to the
          tenant OWNER of a tenant that has more than one seat.

          The old gate was `plan.id >= 4`. A plan id is not a privilege level,
          and the new Recruiter plan takes a fresh id that is >= 4, so every
          paying customer would have seen it. Owner versus seat now comes from
          the shape of the verified profile payload and the seat entitlement from
          the server's own `interviewQuota.seats`; see teamAccess() in
          lib/recruiterOffers.js. Backend ownership checks remain the authority.
        */}
        {team.canManageTeam &&
          <div className="p-4 border-t">
            <Link href={"/dashboard/admin"} data-testid="sidebar-manage-team">
              <Button className="app-upgrade w-full relative overflow-hidden bg-primary hover:bg-primary/90 text-primary-foreground py-2.5 h-auto font-medium text-sm rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.01] group">
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center justify-center gap-2">
                  <Users className="w-5 h-5 text-primary-foreground" />
                  {t("Manage team")}
                </span>
              </Button>
            </Link>
          </div>
        }
      </div>
    </div>
  );
}
